import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { AnyEntity, Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import { StandardFilterButton } from "@app/component/filtering";
import GenericView from "@app/component/view/GenericView/GenericView";
import FunnelSummaryInfo from "@app/scene/deal/type/FunnelSummaryInfo";
import { updateDeal } from "@app/store/deal/actions";
import { isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";

import ViewMode from "../../enum/ViewMode";
import {
  getForecastDeals,
  getForecastViewFunnelSummary,
  getForecastViewMode,
  getForecastViewState,
  getForecastViewTotalFilteredRecordsCount,
  getForecastViewTotalRecordsCount,
  isForecastViewLoading,
} from "../../store";
import {
  fetchForecastDeals,
  initializeForecastView,
  setForecastViewMode,
  updateForecastByDealChanging,
} from "../../store/actions";
import { DatesWithDeals } from "../../store/types/ForecastView";
import { dealForecastViewAnalytics } from "../../util/dealTableAnalytics";
import filterOperatorsForListAndForecast from "../../util/filterOperatorsForListAndForecast";
import CardSorter from "../CardSorter";
import Subheader from "../Subheader";
import Toolbar from "../Toolbar";
import QuickMultiFunnelFilter from "../Toolbar/QuickMultiFunnelFilter";
import QuickOwnerFilter from "../Toolbar/QuickOwnerFilter";
import TotalValues from "../TotalValues";

import ForecastBoard from "./component/ForecastBoard";
import ForecastViewSelection from "./component/ForecastViewSelection";
import SelectClosingDateModal from "./component/SelectClosingDateModal";
import ForecastViewMode from "./enum/ForecastViewMode";
import styles from "./ForecastPage.module.scss";
import ForecastViewState from "./types/ForecastViewState";

interface Props {
  fetcher: typeof fetchForecastDeals.request;
  funnelSummary: FunnelSummaryInfo;
  groupedDeals: DatesWithDeals;
  initialize: typeof initializeForecastView.request;
  isMember?: boolean;
  loading?: boolean;
  onUpdateDeal: typeof updateDeal.request;
  onUpdateViewMode: (viewMode: ForecastViewMode) => void;
  subscribe: typeof subscribe;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
  updateDealData: typeof updateForecastByDealChanging.request;
  viewMode: ForecastViewMode;
  viewState: ForecastViewState;
}

const ForecastPage: React.FC<Props> = ({
  fetcher,
  funnelSummary,
  groupedDeals,
  initialize,
  isMember,
  loading,
  onUpdateDeal,
  onUpdateViewMode,
  subscribe,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
  updateDealData,
  viewMode,
  viewState,
}) => {
  useEffect(() => {
    initialize();
  }, [initialize]);

  const [dealToEdit, setDealToEdit] = useState<Deal | undefined>();
  const [closingDateToEdit, setClosingDateToEdit] = useState<Date | undefined>();

  const handleDropClosingDateToEdit = useCallback(() => setDealToEdit(undefined), [setDealToEdit]);

  const handleDrop = useCallback(
    (date: Date | undefined, deal: Deal) => {
      setClosingDateToEdit(date);
      setDealToEdit(deal);
    },
    [setDealToEdit, setClosingDateToEdit]
  );

  const handleSetClosingDate = useCallback(
    (closingDate: string | undefined) => {
      if (dealToEdit) {
        onUpdateDeal({ ...dealToEdit, closingDate: closingDate ?? null });
      }
      setDealToEdit(undefined);
      dealForecastViewAnalytics?.using(["Drag and Drop Deal", "Change Close Date"], closingDate);
    },
    [dealToEdit, setDealToEdit, onUpdateDeal]
  );

  useEffect(() => {
    const listener = {
      onAdd: (entityType: EntityType, entities: AnyEntity[]) => updateDealData(entities as Deal[]),
      onDelete: (entityType: EntityType, entityIds: AnyEntity["id"][]) =>
        updateDealData(
          Object.values(groupedDeals).reduce<Deal[]>(
            (result, { deals: data }) => [
              ...result,
              ...data.filter(({ id }) => entityIds.includes(id)),
            ],
            []
          )
        ),
      onUpdate: (entityType: EntityType, entities: AnyEntity[]) =>
        updateDealData(entities as Deal[]),
    };
    subscribe({ entityTypes: [EntityType.DEAL], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.DEAL], listener });
    };
  }, [groupedDeals, fetcher, subscribe, unsubscribe, updateDealData]);

  const hiddenFilterFields = useMemo(
    () =>
      [
        dealFieldModel.getByName("closingDate"),
        ...dealFieldModel.fields.filter(
          (field) =>
            (field.hasFeature(FieldFeature.NON_LIST_VIEW) &&
              !field.hasAnyFeature(FieldFeature.VIEW_AS_FIELD, FieldFeature.TEAM_FIELD)) ||
            (isMember && field.hasFeature(FieldFeature.VIEW_AS_FIELD))
        ),
      ].filter(isDefined),
    [isMember]
  );

  const hiddenSavedFilterFields = useMemo(
    () => [dealFieldModel.getByName("closingDate")].filter(isDefined),
    []
  );

  const handleViewModeChange = useCallback(
    (viewMode) => {
      onUpdateViewMode(viewMode);
      dealForecastViewAnalytics?.clicked([
        viewMode === ForecastViewMode.MONTH ? "View By Month" : "View By Quarter",
      ]);
    },
    [onUpdateViewMode]
  );

  return (
    <AnalyticsContext.Provider value={dealForecastViewAnalytics}>
      <GenericView
        entityType={EntityType.DEAL}
        fetcher={fetcher}
        fieldModel={dealFieldModel}
        viewState={viewState}
      >
        <Subheader totalFilteredRecords={totalFilteredRecords} totalRecords={totalRecords} />
        <div className={styles.container}>
          <Toolbar
            extra={[
              <CardSorter key="sorter" />,
              <ForecastViewSelection
                key="viewType"
                onChange={handleViewModeChange}
                value={viewMode}
              />,
              <QuickOwnerFilter key="ownerFilter" />,
              <QuickMultiFunnelFilter key="funnelFilter" />,
            ]}
            filterButton={
              <StandardFilterButton
                context="dealPage"
                entityType={EntityType.DEAL}
                filterOperators={filterOperatorsForListAndForecast}
                hiddenFields={hiddenFilterFields}
                implicitFilterFields={hiddenSavedFilterFields}
              />
            }
            viewMode={ViewMode.FORECAST}
          />
          <TotalValues dealsCount={totalFilteredRecords} funnelSummary={funnelSummary} />
          <ForecastBoard
            className={styles.board}
            forecastViewMode={viewMode}
            groupedDeals={groupedDeals}
            loading={loading}
            onDrop={handleDrop}
          />
          {dealToEdit && (
            <SelectClosingDateModal
              deal={dealToEdit}
              forecastViewMode={viewMode}
              onHide={handleDropClosingDateToEdit}
              onSelect={handleSetClosingDate}
              targetDate={closingDateToEdit}
            />
          )}
        </div>
      </GenericView>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnelSummary: getForecastViewFunnelSummary(state),
  groupedDeals: getForecastDeals(state),
  isMember: isCurrentUserMember(state),
  loading: isForecastViewLoading(state),
  totalFilteredRecords: getForecastViewTotalFilteredRecordsCount(state),
  totalRecords: getForecastViewTotalRecordsCount(state),
  viewMode: getForecastViewMode(state),
  viewState: getForecastViewState(state),
});

const mapDispatchToProps = {
  fetcher: fetchForecastDeals.request,
  initialize: initializeForecastView.request,
  onUpdateDeal: updateDeal.request,
  onUpdateViewMode: setForecastViewMode,
  subscribe,
  unsubscribe,
  updateDealData: updateForecastByDealChanging.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ForecastPage);
